/* eslint-disable */
import DashIcon from "components/icons/DashIcon";
import { Box, Flex, Text, Tooltip } from "components/ui";
import { useSuggestions } from "contexts/SuggestionsContext";
import { useTranslation } from "hooks/useTranslation";
import { useState, type ReactElement } from "react";
import { MdExpandMore } from "react-icons/md";
import { Link, useLocation } from "react-router-dom";
import { authService } from "services/authService";
import { getThemeColor } from "theme/colors";

export const SidebarLinks = (props: {
  routes: RoutesType[];
  collapsed?: boolean;
}): ReactElement => {
  let location = useLocation();
  const { t } = useTranslation();

  const { routes, collapsed = false } = props;
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({});
  const companyFeatures = authService.getCurrentUser()?.company?.features ?? [];
  const { maskingPendingCount } = useSuggestions();

  // verifies if routeName is the one active (in browser input)
  const activeRoute = (routeName: string) => {
    return location.pathname.includes(routeName);
  };

  const toggleSection = (sectionName: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  };

  const createLinks = (routes: RoutesType[]) => {
    return routes.map((route, index) => {
      // Skip routes marked as invisible
      if (route.invisible) {
        return null;
      }

      // Skip routes requiring a feature the company doesn't have enabled
      if (
        route.requiredFeature &&
        !companyFeatures.includes(route.requiredFeature)
      ) {
        return null;
      }

      // Handle sections with children
      if (route.isSection && route.children) {
        const isExpanded = expandedSections[route.name] ?? false; // Default to collapsed
        const hasActiveChild = route.children.some(
          (child) => !child.invisible && activeRoute(child.path),
        );

        const sectionContent = (
          <Box key={index}>
            <Box
              borderRadius="md"
              _hover={{ bg: getThemeColor("sidebar.hoverBg") }}
            >
              <Flex
                as="li"
                my={0}
                align="center"
                px={collapsed ? 0 : 8}
                py={2.5}
                justify={collapsed ? "center" : "flex-start"}
                position="relative"
                cursor="pointer"
                onClick={() => !collapsed && toggleSection(route.name)}
                w="full"
              >
                <Box
                  fontWeight={hasActiveChild ? "bold" : "medium"}
                  color={
                    hasActiveChild
                      ? { base: "brand.500", _dark: "white" }
                      : getThemeColor("sidebar.linkColor")
                  }
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  position="relative"
                >
                  {route.icon ? route.icon : <DashIcon />}
                  {route.name === "app.navigation.security" &&
                    maskingPendingCount > 0 && (
                      <Tooltip
                        content="Masking rules need your review"
                        positioning={{ placement: "right" }}
                      >
                        <Box
                          position="absolute"
                          top="-2px"
                          right="-2px"
                          w="8px"
                          h="8px"
                          borderRadius="full"
                          bg="orange.400"
                          flexShrink={0}
                        />
                      </Tooltip>
                    )}
                </Box>
                {!collapsed && (
                  <>
                    <Text
                      ml={4}
                      fontWeight={hasActiveChild ? "bold" : "medium"}
                      color={
                        hasActiveChild
                          ? getThemeColor("sidebar.linkActiveColor")
                          : getThemeColor("sidebar.linkColor")
                      }
                      flex={1}
                    >
                      {t(route.name)}
                    </Text>
                    <Box
                      color={getThemeColor("sidebar.linkColor")}
                      transition="transform 0.2s"
                      transform={isExpanded ? "rotate(0deg)" : "rotate(-90deg)"}
                    >
                      <MdExpandMore size={20} />
                    </Box>
                  </>
                )}
                {hasActiveChild && (
                  <Box
                    position="absolute"
                    right={0}
                    top="50%"
                    transform="translateY(-50%)"
                    h={9}
                    w={1}
                    borderRadius="lg"
                    bg={getThemeColor("sidebar.linkActiveBg")}
                  />
                )}
              </Flex>
            </Box>

            {/* Render children if expanded and not collapsed */}
            {!collapsed && isExpanded && (
              <Box pl={8}>
                {route.children.map((child, childIndex) => {
                  if (child.invisible) return null;
                  if (
                    child.requiredFeature &&
                    !companyFeatures.includes(child.requiredFeature)
                  )
                    return null;

                  const isChildActive = activeRoute(child.path);
                  return (
                    <Link
                      key={childIndex}
                      to={child.layout + "/" + child.path}
                      style={{ textDecoration: "none" }}
                    >
                      <Box position="relative">
                        <Flex
                          as="li"
                          my={0}
                          cursor="pointer"
                          align="center"
                          px={4}
                          py={2}
                          justify="flex-start"
                          position="relative"
                          borderRadius="md"
                          _hover={{ bg: getThemeColor("sidebar.hoverBg") }}
                        >
                          <Box
                            fontSize="sm"
                            fontWeight={isChildActive ? "semibold" : "normal"}
                            color={
                              isChildActive
                                ? { base: "brand.500", _dark: "white" }
                                : getThemeColor("sidebar.linkColor")
                            }
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            position="relative"
                          >
                            {child.icon ? child.icon : <DashIcon />}
                            {child.name === "app.navigation.dataMasking" &&
                              maskingPendingCount > 0 && (
                                <Tooltip
                                  content="Masking rules need your review"
                                  positioning={{ placement: "right" }}
                                >
                                  <Box
                                    position="absolute"
                                    top="-2px"
                                    right="-2px"
                                    w="8px"
                                    h="8px"
                                    borderRadius="full"
                                    bg="orange.400"
                                    flexShrink={0}
                                  />
                                </Tooltip>
                              )}
                          </Box>
                          <Text
                            ml={3}
                            fontSize="sm"
                            fontWeight={isChildActive ? "semibold" : "normal"}
                            color={
                              isChildActive
                                ? getThemeColor("sidebar.linkActiveColor")
                                : getThemeColor("sidebar.linkColor")
                            }
                          >
                            {t(child.name)}
                          </Text>
                          {isChildActive && (
                            <Box
                              position="absolute"
                              right={0}
                              top="50%"
                              transform="translateY(-50%)"
                              h={6}
                              w={1}
                              borderRadius="lg"
                              bg={getThemeColor("sidebar.linkActiveBg")}
                            />
                          )}
                        </Flex>
                      </Box>
                    </Link>
                  );
                })}
              </Box>
            )}
          </Box>
        );

        // Wrap with tooltip when collapsed
        if (collapsed) {
          return (
            <Tooltip
              key={index}
              content={t(route.name)}
              positioning={{ placement: "right" }}
            >
              {sectionContent}
            </Tooltip>
          );
        }

        return sectionContent;
      }

      // Regular route items (non-section)
      if (
        route.layout === "/admin" ||
        route.layout === "/auth" ||
        route.layout === "/rtl"
      ) {
        const isActive = activeRoute(route.path);
        const linkContent = (
          <Link
            key={index}
            to={route.layout + "/" + route.path}
            style={{ textDecoration: "none" }}
          >
            <Box position="relative">
              <Flex
                as="li"
                my={0}
                cursor="pointer"
                align="center"
                px={collapsed ? 0 : 8}
                py={2.5}
                justify={collapsed ? "center" : "flex-start"}
                position="relative"
                borderRadius="md"
                _hover={{ bg: getThemeColor("sidebar.hoverBg") }}
              >
                <Box
                  fontWeight={isActive ? "bold" : "medium"}
                  color={
                    isActive
                      ? { base: "brand.500", _dark: "white" }
                      : getThemeColor("sidebar.linkColor")
                  }
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {route.icon ? route.icon : <DashIcon />}
                </Box>
                {!collapsed && (
                  <Text
                    ml={4}
                    fontWeight={isActive ? "bold" : "medium"}
                    color={
                      isActive
                        ? getThemeColor("sidebar.linkActiveColor")
                        : getThemeColor("sidebar.linkColor")
                    }
                  >
                    {t(route.name)}
                  </Text>
                )}
                {isActive && (
                  <Box
                    position="absolute"
                    right={0}
                    top="50%"
                    transform="translateY(-50%)"
                    h={9}
                    w={1}
                    borderRadius="lg"
                    bg={getThemeColor("sidebar.linkActiveBg")}
                  />
                )}
              </Flex>
            </Box>
          </Link>
        );

        // Wrap with tooltip when collapsed
        if (collapsed) {
          return (
            <Tooltip
              key={index}
              content={t(route.name)}
              positioning={{ placement: "right" }}
            >
              {linkContent}
            </Tooltip>
          );
        }

        return linkContent;
      }
    });
  };

  return <>{createLinks(routes)}</>;
};

export default SidebarLinks;
